/**
 * Recently Viewed — display component
 * Loaded only on the recently-viewed.liquid section.
 * Fetches each stored handle's card via the Section Rendering API and
 * injects it into the grid, then reconciles slider classes/counter.
 */
if (!customElements.get('recently-viewed-products')) {
  class RecentlyViewedProducts extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('ul');
      if (!this.list) return;

      this.columnsDesktop = parseInt(this.dataset.columnsDesktop || '4', 10);
      this.columnsMobile = parseInt(this.dataset.columnsMobile || '2', 10);
      this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
      this.swipeOnMobile = this.dataset.swipeOnMobile === 'true';
      this.excludeCurrent = this.dataset.excludeCurrent === 'true';
      this.count = 0;

      this.onProductSwapped = this.onProductSwapped.bind(this);
      window.addEventListener('product:swapped', this.onProductSwapped);

      this.currentProductHandle = this.currentHandle();
      this.render();
    }

    disconnectedCallback() {
      window.removeEventListener('product:swapped', this.onProductSwapped);
    }

    currentHandle() {
      const match = window.location.pathname.match(/\/products\/([a-zA-Z0-9\-_%]+)/);
      return match ? decodeURIComponent(match[1]) : null;
    }

    handleFromUrl(url) {
      try {
        const path = new URL(url, window.location.origin).pathname;
        const match = path.match(/\/products\/([a-zA-Z0-9\-_%]+)/);
        return match ? decodeURIComponent(match[1]) : null;
      } catch (e) {
        return null;
      }
    }

    // Fired the moment a swatch swap finishes (no page reload happens for
    // that action, so this is the only signal we get). Independent of
    // localStorage timing — reads the handle straight from the swap's URL.
    async onProductSwapped(event) {
      const newHandle = event && event.detail && this.handleFromUrl(event.detail.url);
      if (!newHandle) return;

      const previousHandle = this.currentProductHandle;
      this.currentProductHandle = newHandle;
      if (newHandle === previousHandle) return;

      if (this.excludeCurrent) {
        // The product just swapped into is now "the current product" —
        // hide its card if it happens to already be in the grid.
        this.removeCard(newHandle);
        // The product swapped away from is no longer current — it should
        // now show up in the recently-viewed grid if it isn't already.
        if (previousHandle) await this.addCard(previousHandle, { prepend: true });
      } else {
        await this.addCard(newHandle, { prepend: true });
      }
    }

    removeCard(handle) {
      if (!this.list) return;
      const existing = this.list.querySelector('[data-handle="' + CSS.escape(handle) + '"]');
      if (existing) {
        existing.remove();
        this.count = Math.max(0, this.count - 1);
        this.reconcileSlider(this.count);
        if (this.count === 0) this.hidden = true;
      }
    }

    async addCard(handle, options) {
      if (!this.list) return;
      const alreadyPresent = this.list.querySelector('[data-handle="' + CSS.escape(handle) + '"]');
      if (alreadyPresent) return;

      const html = await fetch('/products/' + handle + '?section_id=' + this.dataset.cardSectionId)
        .then((res) => (res.ok ? res.text() : null))
        .catch(() => null);
      if (!html) return;

      const template = document.createElement('template');
      template.innerHTML = html.trim();
      const li = template.content.querySelector('li.grid__item');
      if (!li) return;

      if (options && options.prepend) {
        this.list.insertBefore(li, this.list.firstChild);
      } else {
        this.list.appendChild(li);
      }

      this.count += 1;
      this.hidden = false;
      this.reconcileSlider(this.count);
    }

    async render() {
      let handles = (window.RecentlyViewed && window.RecentlyViewed.getHandles()) || [];

      if (this.excludeCurrent) {
        const current = this.currentHandle();
        if (current) handles = handles.filter((h) => h !== current);
      }

      if (!handles.length) {
        this.hidden = true;
        return;
      }

      const results = await Promise.all(
        handles.map((handle) =>
          fetch('/products/' + handle + '?section_id=' + this.dataset.cardSectionId)
            .then((res) => (res.ok ? res.text() : null))
            .catch(() => null)
        )
      );

      const fragment = document.createDocumentFragment();
      const seen = new Set();

      results.forEach((html) => {
        if (!html) return;
        const template = document.createElement('template');
        template.innerHTML = html.trim();

        // The Section Rendering API always wraps the section's output in
        // <div id="shopify-section-{id}" class="shopify-section">, and the
        // section also repeats its own <link rel="stylesheet"> tags. Pull
        // just the <li> back out so it sits as a direct child of the <ul>
        // (grid CSS targets ul > li directly) and drop the rest.
        const li = template.content.querySelector('li.grid__item');
        if (!li) return;

        const handle = li.dataset.handle;
        if (handle && seen.has(handle)) return; // dedupe repeated views
        if (handle) seen.add(handle);

        this.count += 1;
        fragment.appendChild(li);
      });

      if (!this.count) {
        this.hidden = true;
        return;
      }

      this.list.appendChild(fragment);
      this.hidden = false;
      this.reconcileSlider(this.count);
    }

    reconcileSlider(count) {
      const needsMobileSlider = this.swipeOnMobile && count > this.columnsMobile;
      const needsDesktopSlider = this.enableDesktopSlider && count > this.columnsDesktop;

      if (needsMobileSlider || needsDesktopSlider) {
        this.list.classList.add('slider');
        if (needsDesktopSlider) this.list.classList.add('slider--desktop');
        if (needsMobileSlider) this.list.classList.add('slider--tablet', 'grid--peek');

        const sliderComponent = this.querySelector('slider-component');
        if (sliderComponent) sliderComponent.classList.add('slider-component-desktop');

        const totalEl = this.querySelector('.slider-counter--total');
        if (totalEl) totalEl.textContent = String(count);

        // Let slider-component (already defined by the theme) pick up the
        // freshly-added slides on next tick.
        requestAnimationFrame(() => {
          window.dispatchEvent(new Event('resize'));
        });
      } else {
        const buttons = this.querySelector('.slider-buttons');
        if (buttons) buttons.hidden = true;
      }
    }
  }

  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}